<template>
	<div class="w-full">
		<!-- Modals -->
		<Modal :show="showUpdateModal" content_class="w-full max-w-2xl mx-auto">
			<template slot="content">
				<div class="bg-white rounded-lg border border-border p-6">
					<div class="flex items-center justify-between mb-6">
						<div class="flex items-center gap-3">
							<i
								:class="{
									'fas fa-user-edit': type == 1,
									'fas fa-shield-alt': type == 2,
									'fas fa-lock': type == 3,
								}"
								class="text-primary text-xl"
							></i>
							<h2
								class="text-xl lg:text-2xl font-bold text-text-primary tracking-tight"
							>
								<span v-if="type == 1">Update Profile</span>
								<span v-else-if="type == 2"
									>Security Questions</span
								>
								<span v-else-if="type == 3"
									>Change Password</span
								>
							</h2>
						</div>
						<button
							class="text-text-secondary hover:text-text-primary transition-colors p-2"
							@click="closeModal(0)"
						>
							<i class="fas fa-times text-lg"></i>
						</button>
					</div>

					<div class="space-y-6">
						<div v-if="type == 1">
							<Form
								classBinding="grid grid-cols-1 gap-4"
								:ref="Profile.forms.update_profile.name"
								:forms="Profile.forms.update_profile.forms"
								:formData="Profile.forms.update_profile.data"
							/>
							<button
								class="w-full py-3 text-base lg:text-lg font-medium text-white bg-primary hover:bg-primary-light rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
								:disabled="loading"
								@click="
									updateProfile({
										ref: $refs[
											Profile.forms.update_profile.name
										],
										router: $router,
									})
								"
							>
								<i class="fas fa-save"></i>
								Update Profile
								<div
									v-show="loading"
									class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
								></div>
							</button>
						</div>

						<div v-if="type === 2">
							<Form
								classBinding="grid grid-cols-1 gap-4"
								:ref="Profile.forms.security_questions.name"
								:forms="Profile.forms.security_questions.forms"
								:formData="
									Profile.forms.security_questions.data
								"
							/>
							<button
								class="w-full py-3 text-base lg:text-lg font-medium text-white bg-primary hover:bg-primary-light rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
								:disabled="loading"
								@click="
									updateSecurityQuestion({
										ref: $refs[
											Profile.forms.security_questions
												.name
										],
										router: $router,
									})
								"
							>
								<i class="fas fa-shield-alt"></i>
								Update Questions
								<div
									v-show="loading"
									class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
								></div>
							</button>
						</div>

						<div v-if="type === 3">
							<Form
								classBinding="grid grid-cols-1 gap-4"
								:ref="Profile.forms.change_password.name"
								:forms="Profile.forms.change_password.forms"
								:formData="Profile.forms.change_password.data"
							/>
							<button
								class="w-full py-3 text-base lg:text-lg font-medium text-white bg-primary hover:bg-primary-light rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
								:disabled="loading"
								@click="
									updatePassword({
										ref: $refs[
											Profile.forms.change_password.name
										],
										router: $router,
									})
								"
							>
								<i class="fas fa-lock"></i>
								Change Password
								<div
									v-show="loading"
									class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
								></div>
							</button>
						</div>
					</div>
				</div>
			</template>
		</Modal>

		<Modal :show="showInviteForm" content_class="w-full max-w-md mx-auto">
			<template slot="content">
				<div class="bg-white rounded-lg border border-border p-6">
					<div class="flex items-center justify-between mb-6">
						<div class="flex items-center gap-3">
							<i
								class="fas fa-user-plus text-primary text-xl"
							></i>
							<h2
								class="text-xl lg:text-2xl font-bold text-text-primary tracking-tight"
							>
								Invite Friends
							</h2>
						</div>
						<button
							class="text-text-secondary hover:text-text-primary transition-colors p-2"
							@click="$store.commit('showInviteForm', false)"
						>
							<i class="fas fa-times text-lg"></i>
						</button>
					</div>

					<div class="space-y-6">
						<Form
							classBinding="grid grid-cols-1 gap-4"
							:ref="Profile.forms.send_invite.name"
							:forms="Profile.forms.send_invite.forms"
							:formData="Profile.forms.send_invite.data"
						/>
						<button
							class="w-full py-3 text-base lg:text-lg font-medium text-white bg-primary hover:bg-primary-light rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
							:disabled="loading"
							@click="
								sendInvite({
									ref: $refs[Profile.forms.send_invite.name],
								})
							"
						>
							<i class="fas fa-paper-plane"></i>
							Send Invite
							<div
								v-show="loading"
								class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
							></div>
						</button>
					</div>
				</div>
			</template>
		</Modal>

		<ViewScheme />
		<ViewSchemeDetails />

		<!-- Header Section -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary tracking-tight mb-2"
			>
				My Profile
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				Manage your account settings and preferences
			</p>
		</div>

		<!-- Stats Cards -->
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
			<div
				class="bg-white border border-border rounded-lg p-4 lg:p-5 hover:shadow-md transition-shadow"
			>
				<div class="flex items-center gap-3">
					<div
						class="w-12 h-12 bg-surface rounded-lg flex items-center justify-center"
					>
						<i class="fas fa-envelope text-primary text-lg"></i>
					</div>
					<div>
						<p
							class="text-xl lg:text-2xl font-bold text-text-primary"
						>
							{{
								utils.numberWithCommas(
									invite_summary.invites.no_invited,
								)
							}}
						</p>
						<p
							class="text-xs lg:text-sm text-text-secondary uppercase tracking-wide"
						>
							Total Invites
						</p>
					</div>
				</div>
			</div>
			<div
				class="bg-white border border-border rounded-lg p-4 lg:p-5 hover:shadow-md transition-shadow"
			>
				<div class="flex items-center gap-3">
					<div
						class="w-12 h-12 bg-surface rounded-lg flex items-center justify-center"
					>
						<i class="fas fa-check-circle text-primary text-lg"></i>
					</div>
					<div>
						<p
							class="text-xl lg:text-2xl font-bold text-text-primary"
						>
							{{
								utils.numberWithCommas(
									invite_summary.referrals.success,
								)
							}}
						</p>
						<p
							class="text-xs lg:text-sm text-text-secondary uppercase tracking-wide"
						>
							Successful
						</p>
					</div>
				</div>
			</div>
			<div
				class="bg-white border border-border rounded-lg p-4 lg:p-5 hover:shadow-md transition-shadow"
			>
				<div class="flex items-center gap-3">
					<div
						class="w-12 h-12 bg-surface rounded-lg flex items-center justify-center"
					>
						<i class="fas fa-clock text-primary text-lg"></i>
					</div>
					<div>
						<p
							class="text-xl lg:text-2xl font-bold text-text-primary"
						>
							{{
								utils.numberWithCommas(
									invite_summary.invites.no_invited,
								)
							}}
						</p>
						<p
							class="text-xs lg:text-sm text-text-secondary uppercase tracking-wide"
						>
							Pending
						</p>
					</div>
				</div>
			</div>
			<div
				class="bg-white border border-border rounded-lg p-4 lg:p-5 hover:shadow-md transition-shadow"
			>
				<div class="flex items-center gap-3">
					<div
						class="w-12 h-12 bg-surface rounded-lg flex items-center justify-center"
					>
						<i class="fas fa-sitemap text-primary text-lg"></i>
					</div>
					<div>
						<p
							class="text-xl lg:text-2xl font-bold text-text-primary"
						>
							{{ utils.numberWithCommas(total_schemes) }}
						</p>
						<p
							class="text-xs lg:text-sm text-text-secondary uppercase tracking-wide"
						>
							Total Schemes
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Main Content Grid -->
		<div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
			<!-- Left Column -->
			<div class="lg:col-span-2 space-y-6">
				<!-- Quick Actions -->
				<div
					class="bg-white border border-border rounded-lg p-4 lg:p-6"
				>
					<h3
						class="text-lg lg:text-xl font-semibold text-text-primary mb-4 lg:mb-5 flex items-center gap-2"
					>
						<i class="fas fa-bolt text-primary"></i> Quick Actions
					</h3>
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
						<div
							class="p-3 lg:p-4 bg-surface rounded-lg hover:bg-primary/5 cursor-pointer transition-colors flex items-center gap-3"
							@click="$store.commit('showInviteForm', true)"
						>
							<div
								class="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center"
							>
								<i class="fas fa-user-plus text-base"></i>
							</div>
							<span
								class="text-sm lg:text-base font-medium text-text-primary"
								>Invite Friends</span
							>
						</div>
						<div
							class="p-3 lg:p-4 bg-surface rounded-lg hover:bg-primary/5 cursor-pointer transition-colors flex items-center gap-3"
							@click="$store.commit('showInviteForm', true)"
						>
							<div
								class="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center"
							>
								<i class="fas fa-list-ul text-base"></i>
							</div>
							<span
								class="text-sm lg:text-base font-medium text-text-primary"
								>View Invites</span
							>
						</div>
					</div>
				</div>

				<!-- Profile Information -->
				<div
					class="bg-white border border-border rounded-lg p-4 lg:p-6"
				>
					<div class="flex items-center justify-between mb-4 lg:mb-5">
						<h3
							class="text-lg lg:text-xl font-semibold text-text-primary flex items-center gap-2"
						>
							<i class="fas fa-info-circle text-primary"></i>
							Profile Information
						</h3>
						<button
							class="text-text-secondary hover:text-primary transition-colors p-2"
							@click="openModal(1)"
						>
							<i class="fas fa-edit text-base"></i>
						</button>
					</div>
					<div
						v-if="
							selectKycData &&
							selectKycData.kraPin &&
							selectKycData.kraPin !== 'N/A' &&
							selectKycData.names
						"
						class="space-y-4"
					>
						<div
							class="flex items-center justify-between py-2 border-b border-border"
						>
							<div class="flex items-center gap-2">
								<i
									class="fas fa-phone text-primary text-base"
								></i>
								<span
									class="text-xs lg:text-sm font-medium text-text-secondary uppercase tracking-wide"
									>Contact</span
								>
							</div>
							<span
								class="text-sm lg:text-base text-text-primary"
								>{{
									selectKycData.email || 'Not provided'
								}}</span
							>
						</div>
						<div
							class="flex items-center justify-between py-2 border-b border-border"
						>
							<div class="flex items-center gap-2">
								<i
									class="fas fa-envelope text-primary text-base"
								></i>
								<span
									class="text-xs lg:text-sm font-medium text-text-secondary uppercase tracking-wide"
									>Email</span
								>
							</div>
							<span
								class="text-sm lg:text-base text-text-primary"
								>{{
									selectKycData.email || 'Not provided'
								}}</span
							>
						</div>
						<div class="flex items-center justify-between py-2">
							<div class="flex items-center gap-2">
								<i
									class="fas fa-user text-primary text-base"
								></i>
								<span
									class="text-xs lg:text-sm font-medium text-text-secondary uppercase tracking-wide"
									>Full Name</span
								>
							</div>
							<span class="text-sm lg:text-base text-text-primary"
								>{{
									selectKycData.names.firstName ||
									selectKycData.names.fname
								}}
								{{
									selectKycData.names.lastName ||
									selectKycData.names.lname
								}}</span
							>
						</div>
					</div>
					<div
						v-else
						class="p-3 lg:p-4 bg-surface rounded-lg text-center text-text-secondary"
					>
						<p class="text-sm lg:text-base">
							No profile information available
						</p>
					</div>
					<div class="pt-4 lg:pt-6 border-t border-border mt-4">
						<button
							class="w-full py-3 text-base lg:text-lg font-medium text-primary bg-primary/5 hover:bg-primary/10 rounded-lg transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
							@click="openModal(3)"
						>
							<i class="fas fa-key"></i> Change Password
						</button>
					</div>
				</div>

				<!-- Security Questions -->
				<div
					class="bg-white border border-border rounded-lg p-4 lg:p-6"
				>
					<div class="flex items-center justify-between mb-4 lg:mb-5">
						<h3
							class="text-lg lg:text-xl font-semibold text-text-primary flex items-center gap-2"
						>
							<i class="fas fa-shield-alt text-primary"></i>
							Security Questions
						</h3>
						<button
							class="text-text-secondary hover:text-primary transition-colors p-2"
							@click="openModal(2)"
						>
							<i class="fas fa-edit text-base"></i>
						</button>
					</div>
					<div class="space-y-3">
						<div
							v-for="(question, index) in securityQuestions"
							:key="index"
							class="flex items-start gap-3 py-2 border-b border-border last:border-b-0"
						>
							<div
								class="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0"
							>
								<i
									class="fas fa-question-circle text-primary text-sm"
								></i>
							</div>
							<p
								class="text-sm lg:text-base text-text-primary leading-relaxed"
							>
								{{ question.question }}
							</p>
						</div>
						<div
							v-if="!securityQuestions.length"
							class="p-3 lg:p-4 bg-surface rounded-lg text-center text-text-secondary"
						>
							<p class="text-sm lg:text-base">
								No security questions available
							</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Right Column - Schemes -->
			<div class="lg:col-span-3">
				<div
					class="bg-white border border-border rounded-lg p-4 lg:p-6 h-full"
				>
					<h3
						class="text-lg lg:text-xl font-semibold text-text-primary mb-4 lg:mb-5 flex items-center gap-2"
					>
						<i class="fas fa-layer-group text-primary"></i> My
						Schemes
					</h3>
					<div
						class="space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar"
					>
						<div
							v-for="(scheme, i) in schemes"
							:key="i"
							class="p-3 lg:p-4 bg-surface border border-border rounded-lg hover:bg-primary/5 cursor-pointer transition-colors"
							@click="viewGroupSchemes(scheme)"
						>
							<div class="flex items-center justify-between mb-2">
								<div class="flex items-center gap-3">
									<img
										:src="
											scheme.scheme_logo
												? api.getDomain() +
													'/' +
													scheme.scheme_logo
												: 'https://api.prod.nexus.ke/static/default-logo.webp'
										"
										alt="Scheme Logo"
										class="w-12 h-12 lg:w-16 lg:h-16 object-contain rounded-lg border border-border bg-white p-2"
									/>
									<div>
										<h4
											class="text-base lg:text-lg font-semibold text-text-primary"
										>
											{{ scheme.scheme_name }}
										</h4>
										<p
											class="text-xs lg:text-sm text-text-secondary uppercase tracking-wide"
										>
											{{ scheme.system_scheme_ref }}
										</p>
									</div>
								</div>
								<i
									class="fas fa-circle text-green-500 text-xs"
								></i>
							</div>
							<p
								class="text-sm lg:text-base text-text-secondary leading-relaxed"
							>
								{{ scheme.scheme_description }}
							</p>
							<div class="flex justify-end">
								<i
									class="fas fa-arrow-right text-primary text-base"
								></i>
							</div>
						</div>
						<div
							v-if="!schemes.length"
							class="p-3 lg:p-4 bg-surface rounded-lg text-center text-text-secondary"
						>
							<p class="text-sm lg:text-base">
								No schemes available
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import profile from '../rsc/stores/profile.store';
import { mapGetters, mapActions } from 'vuex';

export default {
	beforeCreate() {
		Event.$emit('loader:show', true);
	},
	beforeDestroy() {
		this.$store.unregisterModule('profile');
	},
	created() {
		utils.registerModule('profile', profile, this.$store);
	},
	components: {
		Modal: () => import('../templates/others/modal'),
		ViewScheme: () => import('./view-scheme.vue'),
		ViewSchemeDetails: () => import('./view-scheme-details.vue'),
		Form: () => import('@/libs/clarks/components/form'),
	},
	data() {
		return {
			utils,
			api,
		};
	},
	computed: {
		...mapGetters([
			'email',
			'names',
			'total_schemes',
			'schemes',
			'Profile',
			'invite_summary',
			'type',
			'showUpdateModal',
			'showInviteForm',
			'contact',
			'securityQuestions',
			'loading',
		]),
		...mapGetters('auth', ['selectAuthData']),
		...mapGetters('kyc', ['selectKycData']),
	},
	methods: {
		...mapActions([
			'fetchData',
			'fetchInviteSummary',
			'viewGroupSchemes',
			'openModal',
			'sendInvite',
			'closeModal',
			'updateProfile',
			'updateSecurityQuestion',
			'updatePassword',
		]),
	},
	async mounted() {
		utils.registerModule('profile', profile, this.$store);
		await this.fetchInviteSummary();
		this.fetchData();
	},
};
</script>

<style scoped>
.custom-scrollbar {
	scrollbar-width: thin;
	scrollbar-color: #78758b transparent;
}

.custom-scrollbar::-webkit-scrollbar {
	width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
	background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
	background: #78758b;
	border-radius: 9999px;
	transition: background-color 0.2s;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
	background: #160d50;
}

@media (prefers-reduced-motion: reduce) {
	.transition-all,
	.transition-colors,
	.transition-shadow {
		transition: none;
	}
}
</style>
